/**
 * Minimal hash router — no react-router dependency. Pages register routes
 * with the <Route> component; the router renders the first match.
 *
 * Routes are keyed off `location.hash`:
 *   #/        → home
 *   #/onboarding
 *   #/search/results?o=...&d=...&date=...
 *   #/ride/:id
 *   #/publish
 *   #/my/rides
 *   #/my/bookings
 *   #/profile
 *   #/profile/:userId
 *   #/premium
 *
 * Query strings live on the hash (after `?`). Pages read them off
 * `window.location.hash` directly; `path` here is path-only.
 */
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

interface RouterCtx {
  /** Path portion only — never includes the `?query` suffix. */
  path: string;
  /** Search params parsed from the hash. */
  search: URLSearchParams;
  params: Record<string, string>;
  navigate: (to: string) => void;
  /** Pop the history stack to the previous page; falls back to home. */
  back: () => void;
}

const Ctx = createContext<RouterCtx | null>(null);

interface Parsed {
  path: string;
  search: URLSearchParams;
}

function parseHash(): Parsed {
  const h = window.location.hash.replace(/^#/, '');
  // Telegram appends its launch fragment (#tgWebAppData=...) on open. That's
  // not one of our routes — treat any non-"/" hash as the home route, otherwise
  // the home screen renders without matching `/` (which hid the bottom nav).
  if (!h.startsWith('/')) return { path: '/', search: new URLSearchParams() };
  const [rawPath, rawQuery] = h.split('?');
  const norm = rawPath?.startsWith('/') ? rawPath : `/${rawPath ?? ''}`;
  return { path: norm, search: new URLSearchParams(rawQuery ?? '') };
}

/** Full hash (path + ?query), normalized to start with `/`. */
function currentFullHash(): string {
  const h = window.location.hash.replace(/^#/, '');
  // Same launch-fragment handling as parseHash — the initial stack entry is "/".
  return h.startsWith('/') ? h : '/';
}

export function RouterProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<Parsed>(parseHash());
  // Visited-page stack (full hashes) so Back returns to the real previous page
  // rather than always jumping home. A flag prevents a back() from re-pushing.
  const stackRef = useRef<string[]>([currentFullHash()]);
  const isBackRef = useRef(false);

  useEffect(() => {
    const onHash = (): void => {
      const full = currentFullHash();
      if (isBackRef.current) {
        isBackRef.current = false;
      } else if (stackRef.current[stackRef.current.length - 1] !== full) {
        stackRef.current.push(full);
      }
      setState(parseHash());
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const navigate = (to: string): void => {
    const normalized = to.startsWith('/') ? to : `/${to}`;
    if (currentFullHash() === normalized) return;
    window.location.hash = normalized; // hashchange handler records the push
  };

  const back = (): void => {
    const stack = stackRef.current;
    if (stack.length > 1) {
      stack.pop(); // drop current page
      const prev = stack[stack.length - 1]!;
      isBackRef.current = true;
      window.location.hash = prev;
    } else {
      // Entered directly via deep link — nothing to pop. Go home.
      navigate('/');
    }
  };

  return (
    <Ctx.Provider value={{ path: state.path, search: state.search, params: {}, navigate, back }}>
      {children}
    </Ctx.Provider>
  );
}

export function useRouter(): RouterCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useRouter must be used inside <RouterProvider>');
  return v;
}

interface RouteProps {
  pattern: string;
  children: (params: Record<string, string>) => ReactNode;
}

export function Route({ pattern, children }: RouteProps): JSX.Element | null {
  const { path } = useRouter();
  const params = matchPattern(pattern, path);
  if (params === null) return null;
  return <>{children(params)}</>;
}

export function NotFoundRoute({ children }: { children: ReactNode }): JSX.Element | null {
  void children;
  return null;
}

function matchPattern(pattern: string, path: string): Record<string, string> | null {
  const pSeg = pattern.replace(/^\//, '').split('/').filter(Boolean);
  const aSeg = path.replace(/^\//, '').split('/').filter(Boolean);
  if (pSeg.length !== aSeg.length) return null;
  const out: Record<string, string> = {};
  for (let i = 0; i < pSeg.length; i++) {
    const p = pSeg[i]!;
    const a = aSeg[i]!;
    if (p.startsWith(':')) {
      out[p.slice(1)] = decodeURIComponent(a);
    } else if (p !== a) {
      return null;
    }
  }
  return out;
}
